import { useState } from "react";
import { motion } from "framer-motion";
import { Check, LoaderCircle, Send } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim() || !EMAIL_RE.test(form.email.trim())) {
      setError("Please complete every field with a valid email.");
      setState("error");
      return;
    }
    setState("loading");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setState("success");
    } catch {
      setError("Something went wrong. Please try again.");
      setState("error");
    }
  };

  return (
    <section id="contact" className="bg-white py-24 sm:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tighter text-diem-ink">
            Talk to the team.
          </h2>
          <p className="mt-5 text-lg text-diem-slate leading-relaxed max-w-md">
            Questions about Deyem, partnerships or early access for your business — we'd love to hear
            from you.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          {state === "success" ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              className="rounded-3xl border border-diem-ok/30 bg-diem-ok/[0.06] p-8 flex items-center gap-4"
              data-testid="contact-success">
              <span className="w-10 h-10 rounded-full bg-diem-ok flex items-center justify-center shrink-0">
                <Check className="w-5 h-5 text-white" />
              </span>
              <p className="font-semibold text-diem-ink">Message sent. We'll get back to you soon.</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-diem-line bg-diem-paper p-6 sm:p-8 space-y-4" data-testid="contact-form" noValidate>
              <div className="grid sm:grid-cols-2 gap-4">
                <input value={form.name} onChange={set("name")} placeholder="Your name" aria-label="Your name"
                  data-testid="contact-name-input"
                  className="rounded-2xl border border-diem-line bg-white px-5 py-3.5 text-[15px] font-medium text-diem-ink placeholder:text-diem-muted outline-none focus:border-diem-blue transition-colors duration-300" />
                <input value={form.email} onChange={set("email")} placeholder="you@example.com" type="email" aria-label="Your email"
                  data-testid="contact-email-input"
                  className="rounded-2xl border border-diem-line bg-white px-5 py-3.5 text-[15px] font-medium text-diem-ink placeholder:text-diem-muted outline-none focus:border-diem-blue transition-colors duration-300" />
              </div>
              <textarea value={form.message} onChange={set("message")} placeholder="How can we help?" rows={4} aria-label="Your message"
                data-testid="contact-message-input"
                className="w-full rounded-2xl border border-diem-line bg-white px-5 py-3.5 text-[15px] font-medium text-diem-ink placeholder:text-diem-muted outline-none focus:border-diem-blue transition-colors duration-300 resize-none" />
              {state === "error" && (
                <p className="text-sm font-semibold text-diem-err" data-testid="contact-error">{error}</p>
              )}
              <button type="submit" disabled={state === "loading"}
                data-testid="contact-submit-button"
                className="group inline-flex items-center gap-2 rounded-full bg-diem-blue text-white font-semibold px-7 py-3.5 shadow-[0_10px_30px_rgba(0,85,255,0.4)] hover:shadow-[0_14px_40px_rgba(0,85,255,0.5)] active:scale-[0.98] transition-[box-shadow,transform] duration-300 disabled:opacity-70">
                {state === "loading" ? <LoaderCircle className="w-5 h-5 animate-spin" /> : (
                  <>Send message <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
                )}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
